import { ArrowForward, DeleteOutline, DescriptionOutlined } from '@mui/icons-material'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import './ViewDocuments.css'
import { useEffect, useState } from 'react'
import { orderDocType } from '../types'
import { useUser } from '../hooks'
import axios from 'axios'

const COLUMS_WIDTHS = {
  fileName: undefined,
  view: '100px',
  delete: '75px',
}

export default function ViewDocuments({
  orderId,
  open,
  setOpen,
}: {
  orderId: string
  open: boolean
  setOpen: (open: boolean) => void
}) {
  const user = useUser()
  const SHAREPOINT_LINK = `https://imprestechnology.sharepoint.com/sites/${process.env.REACT_APP_SHAREPOINT_SITE}/Order%20Portal%20Uploads/Forms/AllItems.aspx?id=%2Fsites%2F${process.env.REACT_APP_SHAREPOINT_SITE}%2FOrder%20Portal%20Uploads%2F${orderId}`

  const [documents, setDocuments] = useState<orderDocType>({
    GovernmentAward: [],
    VendorQuote: [],
    IMPRESQuote: [],
    RelatedDoc: [],
  })
  const [deleteDoc, setDeleteDoc] = useState<{ name: string; docType: keyof orderDocType } | null>(null)
  const [deleteStatus, setDeleteStatus] = useState<'loading' | 'error' | ''>('')

  const handleDelete = (name: string, docType: keyof orderDocType) => {
    setDeleteStatus('loading')
    axios
      .delete(`/deleteDocument/${orderId}/${docType}/${name}`)
      .then(() => {
        setDocuments(prev => ({ ...prev, [docType]: prev[docType].filter(doc => doc.name !== name) }))
        setDeleteDoc(null)
        setDeleteStatus('')
      })
      .catch(err => {
        console.error(err)
        setDeleteStatus('error')
      })
  }

  useEffect(() => {
    if (!user.email || !orderId) {
      return
    }

    axios
      .get(`/listDocuments/${orderId}`)
      .then(res => {
        setDocuments(res.data)
      })
      .catch(err => {
        console.error(err)
      })
  }, [user, orderId])

  return (
    <>
      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <div className="view-documents-container">
          <div className="view-documents-header">
            <div className="view-documents-header-left">
              <Typography variant="h6">Documents</Typography>
              <Button
                size="large"
                variant="outlined"
                startIcon={<DescriptionOutlined />}
                onClick={() => window.open(SHAREPOINT_LINK, '_blank')}
              >
                View in Sharepoint
              </Button>
            </div>
            <IconButton onClick={() => setOpen(false)}>
              <ArrowForward />
            </IconButton>
          </div>
          <Divider flexItem />
          <div className="view-documents-body">
            {[
              { label: 'Government Award', key: 'GovernmentAward' },
              { label: 'IMPRES Quote', key: 'IMPRESQuote' },
              { label: 'Vendor Quote', key: 'VendorQuote' },
              { label: 'Misc Docs', key: 'RelatedDoc' },
            ].map(
              ({ label, key }) =>
                documents[key].length > 0 && (
                  <div className="view-documents-type-container">
                    <Typography variant="h6">{label}</Typography>
                    <Table color="primary">
                      <TableHead>
                        <TableRow>
                          <TableCell width={COLUMS_WIDTHS.fileName}>File Name</TableCell>
                          <TableCell width={COLUMS_WIDTHS.view} />
                          <TableCell width={COLUMS_WIDTHS.delete} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {documents[key].map(doc => (
                          <TableRow key={doc.id}>
                            <TableCell>{doc.name}</TableCell>
                            <TableCell>
                              <Button
                                size="large"
                                variant="outlined"
                                onClick={() => window.open(doc.webUrl ?? '', '_blank')}
                              >
                                View
                              </Button>
                            </TableCell>
                            <TableCell>
                              <IconButton onClick={() => setDeleteDoc({ name: doc.name ?? '', docType: key })}>
                                <DeleteOutline color="primary" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ),
            )}
          </div>
          <Dialog fullWidth maxWidth="md" open={!!deleteDoc} onClose={() => setDeleteDoc(null)}>
            <DialogTitle>Delete Document</DialogTitle>
            <DialogContent>
              <Typography>Are you sure you want to delete {deleteDoc?.name}?</Typography>
            </DialogContent>
            <DialogActions>
              <Button variant="outlined" onClick={() => setDeleteDoc(null)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                loading={deleteStatus === 'loading'}
                onClick={() => {
                  handleDelete(deleteDoc?.name ?? '', deleteDoc?.docType ?? '')
                }}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </Drawer>
      <Snackbar open={deleteStatus === 'error'}>
        <Alert severity="error" variant="filled" onClose={() => setDeleteStatus('')}>
          Failed to delete document. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
    </>
  )
}
